import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { useEffect, useState } from 'react';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import 'leaflet-geosearch/dist/geosearch.css';


import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './mapSelector.css';
// Fix Leaflet's default icon path
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function ClickListener({ onLocationSelect }) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      onLocationSelect({ lat, lng });
    },
  });
  return null;
}

// The search bar (free, keyless -- OpenStreetMap's Nominatim via
// leaflet-geosearch) is the primary way to place a milestone: it's the only
// part of this form that can turn typed text into a specific address rather
// than just a postcode's rough centroid, without depending on a paid/
// licensed address-lookup service. Passes the matched result's label back up
// too, so the caller can auto-fill the address field with real text instead
// of leaving it for the user to type by hand.
function SearchBar({ onLocationSelect }) {
  const map = useMap();

  useEffect(() => {
    const provider = new OpenStreetMapProvider();

    const searchControl = new GeoSearchControl({
      provider,
      style: 'bar',
      autoClose: true,
      retainZoomLevel: false,
      animateZoom: true,
      keepResult: false,
    });

    map.addControl(searchControl);

    const container = map.getContainer();

    // Add accessible label and combobox role to the plugin-generated search input
    const searchInput = container.querySelector('.geosearch input');
    if (searchInput) {
      searchInput.setAttribute('aria-label', 'Search for an address');
      searchInput.setAttribute('role', 'combobox');
      searchInput.setAttribute('aria-autocomplete', 'list');
      searchInput.setAttribute('aria-expanded', 'false');
      searchInput.setAttribute('aria-controls', 'geosearch-results');
    }

    // Add listbox role to the results container so screen readers announce suggestions
    const resultsContainer = container.querySelector('.geosearch .results');
    if (resultsContainer) {
      resultsContainer.setAttribute('id', 'geosearch-results');
      resultsContainer.setAttribute('role', 'listbox');
      resultsContainer.setAttribute('aria-label', 'Address suggestions');

      // Observe the results list for changes to tag each result as an option
      const observer = new MutationObserver(() => {
        const items = resultsContainer.querySelectorAll('div');
        const hasResults = items.length > 0;

        if (searchInput) {
          searchInput.setAttribute('aria-expanded', String(hasResults));
        }

        items.forEach((item, index) => {
          item.setAttribute('role', 'option');
          item.setAttribute('aria-posinset', String(index + 1));
          item.setAttribute('aria-setsize', String(items.length));
        });
      });

      observer.observe(resultsContainer, { childList: true, subtree: true });

      // Store observer for cleanup
      searchControl._a11yObserver = observer;
    }

    map.on('geosearch/showlocation', (result) => {
      const { x: lng, y: lat, label } = result.location;
      onLocationSelect({ lat, lng, label });
    });

    return () => {
      if (searchControl._a11yObserver) {
        searchControl._a11yObserver.disconnect();
      }
      map.removeControl(searchControl);
    };
  }, [map, onLocationSelect]);

  return null;
}

export default function MapSelector({ onSelect }) {
  const [position, setPosition] = useState(null);

  const handleSelect = ({ lat, lng, label }) => {
    setPosition([lat, lng]);
    onSelect({ lat, lng, label });
  };

  return (
    <div className="unique-map-container">
      <p className="map-instructions" id="map-instructions">
        Search for an address above, or click on the map to select a location directly.
      </p>
      <MapContainer
        center={[51.5072, -0.1276]}
        zoom={7}
        style={{ height: '400px', width: '100%' }}
        className="unique-leaflet-container"
        role="application"
        aria-label="Interactive map for selecting a location"
        aria-describedby="map-instructions"
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />
        <SearchBar onLocationSelect={handleSelect} />
        <ClickListener onLocationSelect={handleSelect} />
        {position !== null && <Marker position={position} />}
      </MapContainer>
    </div>
  );
}
